import type { CSSProperties } from 'react';

import { Text } from '@actual-app/components/text';

import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';

import { color, num } from './tokens';

/**
 * Garantiza el «€» en una cifra ya formateada con `useFormat` (H10). Con un
 * presupuesto en euros `useFormat` ya lo pone («1.234,56 €», con espacio fino
 * duro U+202F); si el presupuesto no tiene moneda, se añade con el mismo
 * espacio para que el euro se escriba siempre igual.
 */
export function conSimboloEuro(cifra: string): string {
  return /€/.test(cifra) ? cifra : `${cifra}\u202F€`;
}

type ImporteProps = {
  /** Céntimos enteros. */
  valor: number;
  /** «auto»: rojo si es negativo; «signo»: verde/rojo según el signo. */
  tono?: 'auto' | 'signo' | 'ninguno';
  /** Antepone «+» a los positivos. */
  conSigno?: boolean;
  /** Pone el «€» aunque el presupuesto no tenga moneda (`conSimboloEuro`). */
  conEuro?: boolean;
  style?: CSSProperties;
  'data-testid'?: string;
};

/** Importe con el formato del presupuesto (es-ES: «1.234,56 €»), tabular. */
export function Importe({
  valor,
  tono = 'ninguno',
  conSigno = false,
  conEuro = false,
  style,
  'data-testid': testId,
}: ImporteProps) {
  const format = useFormat();
  const cifra = format(valor, 'financial');
  const texto = conEuro ? conSimboloEuro(cifra) : cifra;
  const colorTexto =
    tono === 'ninguno'
      ? undefined
      : valor < 0
        ? color.bad
        : tono === 'signo' && valor > 0
          ? color.ok
          : undefined;
  return (
    <PrivacyFilter>
      <Text
        data-testid={testId}
        style={{
          ...num,
          whiteSpace: 'nowrap',
          ...(colorTexto && { color: colorTexto }),
          ...style,
        }}
      >
        {conSigno && valor > 0 ? '+' : ''}
        {texto}
      </Text>
    </PrivacyFilter>
  );
}
